import { useNavigate } from 'react-router';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { JobPostHeader, ScrollableWrapper } from 'view/pages/createJobPost/components';
import { BackButton, Button, Spinner } from 'view/components';
import TestMultiplySelect from 'view/pages/profile/TestPage/MultiplySelect';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import * as CS from 'view/pages/createJobPost/commonStyled';
import { ArrowIcon } from 'assets';
import { useUpdateJobPostByStep } from 'view/pages/createJobPost/hooks/useUpdateJobPostByStep';
import { useContextSelector as useCS } from 'use-context-selector';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { CreateJobPostBackToHomeModal } from 'view/modals';
import { Location, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import { TUpdate } from 'domain/types';
import Step from '@mui/material/Step';
import { steps } from 'view/layouts/CreateJobPostLayout';
import { useStateMachine } from 'little-state-machine';
import { updateStep3Action } from 'view/pages/createJobPost/helpers/updateStep3Action';

interface IFormInput {
  qualifications: null;
  softSkills: [];
  hardSkills: [];
}

const QualificationsAndSkillsFields = ({
  onDraftSubmit,
  qualificationData,
  isQualificationLoading,
  softSkillsData,
  isSoftSkillsLoading,
  hardSkillsData,
  isHardSkillsLoading,
  isUpdating,
}) => {
  const navigate = useNavigate();
  const location: Location = useLocation();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));
  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );
  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );

  const activeStep = steps.findIndex((step) => {
    return location.pathname.includes(step.path);
  });

  const {
    /* watch, */
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<IFormInput>();

  /* const qualificationsValue: any = watch('qualifications') ?? null; */

  return (
    <>
      <CS.CreateJobPostHeader style={{ justifyContent: 'space-between' }}>
        {matches ? (
          <CS.CJPBackButtonBox>
            <BackButton
              text="Back to Home"
              color="#663497"
              onClick={() => {
                if (update) update({ isOpenBackToHomeModal: true });
              }}
            />
          </CS.CJPBackButtonBox>
        ) : null}
        <CS.CreateJobPostHeader>
          <Button
            variant="outlined"
            text="Save as Draft"
            onClick={handleSubmit(onDraftSubmit)}
            style={{ padding: '8px 20px', width: 'max-content' }}
            disabled={!!Object.keys(errors).length || isUpdating} // || !qualificationsValue?.length
          />
        </CS.CreateJobPostHeader>
      </CS.CreateJobPostHeader>
      {matches ? (
        <div>
          <JobPostHeader
            title={`${createJobPostData?.editStatus ? 'Edit' : 'Create'} Job Post`}
            style={{ marginBottom: '24px', width: 'max-content' }}
          />
          <CS.CustomStepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
            {steps.map((step) => {
              return (
                <Step key={step.label}>
                  <CS.StepLabelText />
                </Step>
              );
            })}
          </CS.CustomStepper>
        </div>
      ) : null}
      <ScrollableWrapper>
        <CS.CreateJobPostMainWrapper>
          <JobPostHeader
            title="Qualifications and Skills"
            subtitle={
              'Highlight the experience needed to succeed in this role. \n Rank desired skills so applicants can match their \n strengths accordingly.'
            }
          />

          <TestMultiplySelect
            name="qualifications"
            label="Qualifications"
            options={qualificationData || []}
            control={control}
            mode="qualification"
            disabled={isQualificationLoading || !qualificationData}
            isShowNoneOption
          />
          <TestMultiplySelect
            name="hardSkills"
            label="Technical Skills"
            options={hardSkillsData || []}
            control={control}
            mode="skill"
            disabled={isHardSkillsLoading || !hardSkillsData}
          />
          <TestMultiplySelect
            name="softSkills"
            label="Soft Skills"
            options={softSkillsData || []}
            control={control}
            mode="skill"
            disabled={isSoftSkillsLoading || !softSkillsData}
          />

          <CS.CJPSubmitBtnWrapper>
            <Button
              variant="outlined"
              text="Back"
              onClick={() => {
                return navigate('/create-job-post/step2', { replace: true });
              }}
              style={{ margin: '32px 0' }}
            />
            <Button
              variant="contained"
              type="submit"
              text="Next"
              style={{ margin: '32px 0' }}
              endIcon={
                isUpdating ? (
                  <Spinner
                    disabled={!!Object.keys(errors).length || isUpdating}
                    variant="contained"
                  />
                ) : (
                  <ArrowIcon rotate="right" color="white" />
                )
              }
              disabled={!!Object.keys(errors).length || isUpdating} // || !qualificationsValue?.length
            />
          </CS.CJPSubmitBtnWrapper>
        </CS.CreateJobPostMainWrapper>
      </ScrollableWrapper>
      <CreateJobPostBackToHomeModal
        onDraftSubmitClick={handleSubmit(onDraftSubmit)}
        endIcon={
          isUpdating ? (
            <Spinner disabled={!!Object.keys(errors).length || isUpdating} variant="contained" />
          ) : null
        }
        disabled={!!Object.keys(errors).length || isUpdating} // || !qualificationsValue?.length
      />
    </>
  );
};

const QualificationsAndSkills = () => {
  const navigate = useNavigate();
  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );
  const { data: qualificationData, isLoading: isQualificationLoading } = useGetDropdownData(
    DropdownsEnum.QUALIFICATION
  );

  const { data: softSkillsData, isLoading: isSoftSkillsLoading } = useGetDropdownData(
    DropdownsEnum.SKILL,
    null,
    'Soft'
  );

  const { data: hardSkillsData, isLoading: isHardSkillsLoading } = useGetDropdownData(
    DropdownsEnum.SKILL,
    null,
    'Hard'
  );

  const { isLoading: isUpdating, updateJobPostByStep } =
    useUpdateJobPostByStep('/create-job-post/step5');

  const getDataToSubmit = (formData: IFormInput) => {
    return {
      qualifications: formData.qualifications,
      skills: [...formData.softSkills, ...formData.hardSkills],
      progressStep: 5,
    };
  };

  const { state, actions } = useStateMachine({
    actions: { updateStep3Action },
  });

  const onSubmit = (formData: IFormInput) => {
    const dataToSend = getDataToSubmit(formData);
    actions.updateStep3Action(dataToSend);
    navigate('/create-job-post/step5');
    /* updateJobPostByStep(dataToSend); */
  };

  const onDraftSubmit = (formData: IFormInput) => {
    const dataToSend = getDataToSubmit(formData);
    const dataToSendWithoutEmptyValues = JSON.parse(
      JSON.stringify({ ...state.data, ...dataToSend })
    );
    dataToSendWithoutEmptyValues.immHiringNeed =
      dataToSendWithoutEmptyValues?.immHiringNeed?.name === 'Yes';
    for (const key in dataToSendWithoutEmptyValues) {
      if (dataToSendWithoutEmptyValues[key] === null || dataToSendWithoutEmptyValues[key] === '') {
        delete dataToSendWithoutEmptyValues[key];
      }
    }
    updateJobPostByStep(
      { ...dataToSendWithoutEmptyValues, editStatus: null, postStatus: 'draft' },
      true
    );
  };

  const defaultValues = {
    qualifications: state.data.qualifications || createJobPostData?.qualifications || null,
    softSkills:
      (state.data?.skills?.length > 0 &&
        state.data?.skills?.filter((item) => {
          return item.skill.group === 'Soft Skills';
        })) ||
      (createJobPostData?.skills?.length > 0 &&
        createJobPostData?.skills?.filter((item) => {
          return item.skill.group === 'Soft Skills';
        })) ||
      [],
    hardSkills:
      (state.data?.skills?.length > 0 &&
        state.data?.skills?.filter((item) => {
          return item.skill.group === 'Hard Skills';
        })) ||
      (createJobPostData?.skills?.length > 0 &&
        createJobPostData?.skills?.filter((item) => {
          return item.skill.group === 'Hard Skills';
        })) ||
      [],
  };

  return (
    <>
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit}
      >
        <QualificationsAndSkillsFields
          onDraftSubmit={onDraftSubmit}
          qualificationData={qualificationData}
          isQualificationLoading={isQualificationLoading}
          softSkillsData={softSkillsData}
          isSoftSkillsLoading={isSoftSkillsLoading}
          hardSkillsData={hardSkillsData}
          isHardSkillsLoading={isHardSkillsLoading}
          isUpdating={isUpdating}
        />
      </FormContainer>
    </>
  );
};

export default QualificationsAndSkills;
